import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { ThemeToggle } from "@/components/common";
import { Button } from "@/components/ui/button";

import logoLight from "@/assets/images/logos/logoLightTransparent.png";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { loginSchema, type LoginFormData } from "@/features/auth/schemas/auth";

/**
 * Login page component with shadcn form validation
 *
 * @returns {React.JSX.Element} Login page component
 */
export default function Login(): React.JSX.Element {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  // Initialize form with react-hook-form and zod validation
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  /**
   * Handles form submission
   */
  const onSubmit = async (values: LoginFormData) => {
    try {
      setError("");
      const response = await login(values.email, values.password);
      if (response.success) {
        await navigate("/dashboard");
      } else {
        setError(response.error || "Error al iniciar sesión. Verifica tus credenciales.");
      }
    } catch (error) {
      setError("Error al iniciar sesión. Verifica tus credenciales.");
      console.error("Login error:", error);
    }
  };

  /**
   * Toggles password visibility
   */
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Password field type based on visibility state
   */
  const passwordFieldType = showPassword ? "text" : "password";

  /**
   * Password visibility icon
   */
  const PasswordIcon = showPassword ? EyeOff : Eye;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-8 px-6 sm:px-8 lg:px-8 safe-top">
      {/* Theme Toggle positioned absolutely */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle size="sm" />
      </div>

      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        <div className="flex flex-col items-center">
          <img src={logoLight} alt="ViewMed" className="h-12 sm:h-14 w-auto mb-4" />
          <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            Iniciar Sesión
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Accede a tu cuenta de administrador
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="mt-6 sm:mt-8 space-y-5 sm:space-y-6 touch-action-manipulation"
          >
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Correo electrónico o usuario</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        inputMode="email"
                        placeholder="usuario o usuario@viewmedonline.com"
                        autoComplete="username"
                        required
                        className="text-base sm:text-sm h-11 sm:h-10"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contraseña</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          {...field}
                          type={passwordFieldType}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          required
                          className="text-base sm:text-sm h-11 sm:h-10 pr-12"
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={handleTogglePassword}
                        className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 tap-target"
                        aria-label={
                          showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                        }
                      >
                        <PasswordIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="w-full min-h-[48px] text-base sm:text-sm touch-action-manipulation"
              >
                {form.formState.isSubmitting && (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                )}
                <LogIn className="mr-2 h-5 w-5 sm:h-4 sm:w-4" />
                Iniciar Sesión
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
